import {noop} from 'lodash';

import {readApiModel} from '../../src/helpers/apiHelpers';
import {ResolveDispatchProps} from '../../src/helpers/reduxHelpers';
import {AnalyticsExportApiModel, parseAnalyticsExport} from '../../src/models/api/analyticsExportModel';
import {ApiAnalyticsJobProgressEvent} from '../../src/models/api/analyticsJobsModel';
import {AnswerFolderModel, ApiAnswerFolder} from '../../src/models/api/answerFolderModel';
import {ApiCalendarEventVersionDataGroup} from '../../src/models/api/dataGroupModel';
import {ApiNotification} from '../../src/models/api/notificationModel';
import {reloadBrowser} from '../../src/services/browser';
import {
  deleteAnswerFolderAction,
  markAnswerFoldersAsStaleAction,
  updateAnswerFolderAction,
} from '../../src/store/data/answerFolders/answerFolderActions';
import {
  answerDeletedAction,
  markAnswersAsStaleAction,
  updateAnswerFromPushAsyncAction,
} from '../../src/store/data/answers/answerActions';
import {deleteAnswerAndFoldersBulkPushAction} from '../../src/store/data/answers/answerBulkActions';
import {fetchCalendarVersionsAsyncAction} from '../../src/store/data/calendarVersions/calendarVersionsActions';
import {updateNotificationsCounter} from '../../src/store/data/counters/countersActions';
import {exportUpdatedReceivedAction} from '../../src/store/panels/analytics/exports/analyticsPanelExportsActions';
import {processAnalyticsJobProgressEventAction} from '../../src/store/panels/analytics/report/analyticsPanelReportActions';
import {refreshCalendarPanelsAsyncAction} from '../../src/store/panels/calendars/calendarsPanelActions';
import {showAppNotificationAction} from '../../src/store/stateless/notificationsActions';
import {PushBaseProps} from './pushCommons';
import {
  makePushEventSubscriptions,
  PushEventDefinition,
  PushEventSubscriptionsDelegate,
  sharedDispatchProps,
} from './pushEventSubscriptions';

/*
 * Types.
 */

export type PushDispatchProps = ResolveDispatchProps<typeof desktopPushContainerDispatchProps>;

/*
 * Events configuration.
 */

export function makeDesktopPushEventSubscriptions(
  props: PushBaseProps & PushDispatchProps,
  subscriptionsDelegate: PushEventSubscriptionsDelegate,
): ReadonlyArray<PushEventDefinition> {
  const sharePushEventSubscriptions = makePushEventSubscriptions(props, subscriptionsDelegate);
  const makeDesktopOnlyPushEventSubscriptions = (): ReadonlyArray<PushEventDefinition> => [
    {
      name: 'num_unseen_notifications_changed',
      handler: (data) => {
        const notificationsCounter = Number(data.num_unseen_notifications);
        if (!props.isNotificationCenterEnabled || !Number.isInteger(notificationsCounter)) {
          return noop;
        }

        return () => props.updateNotificationsCounter(notificationsCounter);
      },
    },
    {
      name: 'canned_answer_updated',
      handler: (data) => {
        const answerId = Number(data);
        return () => props.updateAnswerFromPushAsync(answerId);
      },
    },
    {
      name: 'canned_answer_deleted',
      handler: (data) => {
        const answerId = Number(data);
        return () => props.answerDeleted(answerId);
      },
    },
    {
      name: 'canned_answer_folder_updated',
      handler: (data) => {
        const answerFolder: AnswerFolderModel = readApiModel(ApiAnswerFolder, data);
        return () => props.updateAnswerFolder(answerFolder);
      },
    },
    {
      name: 'canned_answer_folder_deleted',
      handler: (data) => {
        const answerFolderId = Number(data.id);
        return () => props.deleteAnswerFolder(answerFolderId);
      },
    },
    {
      name: 'canned_answers_updated_batch',
      handler: () => () => props.markAnswersAsStale(),
    },
    {
      name: 'canned_answers_deleted_batch',
      handler: (data) => {
        const answerIds = Array.from(data.answer_ids).map((i) => Number(i));
        return () => props.deleteAnswerAndFoldersBulk(answerIds, []);
      },
    },
    {
      name: 'canned_answer_folders_updated_batch',
      handler: () => () => props.markAnswerFoldersAsStale(),
    },
    {
      name: 'canned_answer_folders_deleted_batch',
      handler: (data) => {
        const folderIds = Array.from(data.folder_ids).map((i) => Number(i));
        return () => props.deleteAnswerAndFoldersBulk(folderIds, []);
      },
    },
    {
      name: 'analytics_job_updated',
      handler: (data) => {
        if (!data) {
          return noop;
        }

        const jobProgressEvent = readApiModel(ApiAnalyticsJobProgressEvent, data);
        return () => props.processAnalyticsJobProgressEvent(jobProgressEvent);
      },
    },
    {
      name: 'export_updated',
      handler: (data) => {
        const exportApiItem = data as AnalyticsExportApiModel;
        const exportItem = parseAnalyticsExport(exportApiItem);

        return () => props.exportUpdatedReceived(exportItem);
      },
    },
    {
      name: 'calendar_event_version_updated',
      handler: (data) => {
        const calendarEventVersionDataGroup = readApiModel(ApiCalendarEventVersionDataGroup, data);
        return () => props.updateData(calendarEventVersionDataGroup);
      },
    },
    {
      name: 'calendar_events_imported',
      handler: (data) => {
        // PB-33737: The resulting data fetch is instrumented by investigateFetchedCalendarEventVersionFetch.
        const calendarVersionId = Number(data);
        if (!Number.isInteger(calendarVersionId)) {
          return noop;
        }

        return () => props.refreshCalendarPanelsAsync(calendarVersionId);
      },
    },
    {
      name: 'calendar_versions_updated',
      handler: (namespace) => {
        if (!namespace) {
          return noop;
        }

        return () => props.fetchCalendarVersionsAsync(namespace);
      },
    },
    {
      name: 'app_notification_received',
      handler: (data) => {
        const notification = readApiModel(ApiNotification, data);
        return () => props.showAppNotification(notification);
      },
    },
    {
      name: 'force_browser_reload',
      handler: () => {
        reloadBrowser();
        return noop;
      },
    },
  ];

  return [...sharePushEventSubscriptions, ...makeDesktopOnlyPushEventSubscriptions()];
}

/*
 * Desktop dispatch props.
 */

export const desktopPushContainerDispatchProps = {
  ...sharedDispatchProps,
  ...{
    updateNotificationsCounter,
    showAppNotification: showAppNotificationAction,
    updateAnswerFromPushAsync: updateAnswerFromPushAsyncAction,
    answerDeleted: answerDeletedAction,
    markAnswersAsStale: markAnswersAsStaleAction,
    markAnswerFoldersAsStale: markAnswerFoldersAsStaleAction,
    updateAnswerFolder: updateAnswerFolderAction,
    deleteAnswerFolder: deleteAnswerFolderAction,
    deleteAnswerAndFoldersBulk: deleteAnswerAndFoldersBulkPushAction,
    fetchCalendarVersionsAsync: fetchCalendarVersionsAsyncAction,
    refreshCalendarPanelsAsync: refreshCalendarPanelsAsyncAction,
    processAnalyticsJobProgressEvent: processAnalyticsJobProgressEventAction,
    exportUpdatedReceived: exportUpdatedReceivedAction,
  },
};
